import { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Sidebar from "./components/Sidebar";
import Topbar from "./components/Topbar";
import Footer from "./components/Footer";
import ProtectedRoute from "./components/ProtectedRoute";
import Dashboard from "./pages/Dashboard";
import Suppliers from "./pages/Suppliers";
import PartyDetails from "./pages/PartyDetails";
import Ledger from "./pages/Ledger";
import Inventory from "./pages/Inventory";
import Invoices from "./pages/Invoices";
import Quotations from "./pages/Quotations";
import Reports from "./pages/Reports";
import Settings from "./pages/Settings";
import Login from "./pages/auth/Login";
import Signup from "./pages/auth/Signup";
import ForgotPassword from "./pages/auth/ForgotPassword";
import Customers from "./pages/Customers";
import SuperDashboard from "./pages/SuperDashboard";
import UserCreate from "./pages/UserCreate";
import StoreManagement from "./pages/StoreManagement";
import "./App.css";

function AppContent() {
  const [darkMode, setDarkMode] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { user, loading } = useAuth();

  // Toggle dark mode
  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    localStorage.setItem("darkMode", !darkMode);
  };

  // Check for saved dark mode preference
  useEffect(() => {
    const savedDarkMode = localStorage.getItem("darkMode") === "true";
    setDarkMode(savedDarkMode);
  }, []);

  // Close sidebar on mobile by default
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setSidebarOpen(false);
      } else {
        setSidebarOpen(true);
      }
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className={darkMode ? "dark" : ""}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/unauthorized" element={<Unauthorized />} />

        {/* SUPER_ADMIN Routes */}
        <Route element={<ProtectedRoute allowedRoles={['SUPER_ADMIN']} />}>
          <Route
            path="/super-dashboard"
            element={
              <div className="flex flex-col min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
                <div className="flex flex-1">
                  <Sidebar open={sidebarOpen} setOpen={setSidebarOpen} />
                  <div className="flex flex-col flex-1">
                    <Topbar
                      sidebarOpen={sidebarOpen}
                      setSidebarOpen={setSidebarOpen}
                      darkMode={darkMode}
                      toggleDarkMode={toggleDarkMode}
                    />
                    <main className="flex-1 overflow-y-auto p-4 md:p-6">
                      <SuperDashboard />
                    </main>
                  </div>
                </div>
                <Footer />
              </div>
            }
          />
          <Route
            path="/users/create"
            element={
              <div className="flex flex-col min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
                <div className="flex flex-1">
                  <Sidebar open={sidebarOpen} setOpen={setSidebarOpen} />
                  <div className="flex flex-col flex-1">
                    <Topbar
                      sidebarOpen={sidebarOpen}
                      setSidebarOpen={setSidebarOpen}
                      darkMode={darkMode}
                      toggleDarkMode={toggleDarkMode}
                    />
                    <main className="flex-1 overflow-y-auto p-4 md:p-6">
                      <UserCreate />
                    </main>
                  </div>
                </div>
                <Footer />
              </div>
            }
          />
          <Route
            path="/stores"
            element={
              <div className="flex flex-col min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
                <div className="flex flex-1">
                  <Sidebar open={sidebarOpen} setOpen={setSidebarOpen} />
                  <div className="flex flex-col flex-1">
                    <Topbar
                      sidebarOpen={sidebarOpen}
                      setSidebarOpen={setSidebarOpen}
                      darkMode={darkMode}
                      toggleDarkMode={toggleDarkMode}
                    />
                    <main className="flex-1 overflow-y-auto p-4 md:p-6">
                      <StoreManagement />
                    </main>
                  </div>
                </div>
                <Footer />
              </div>
            }
          />
        </Route>

        {/* ADMIN Routes */}
        <Route element={<ProtectedRoute allowedRoles={['ADMIN']} />}>
          <Route
            path="/stores/:storeId/dashboard"
            element={
              <div className="flex flex-col min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
                <div className="flex flex-1">
                  <Sidebar open={sidebarOpen} setOpen={setSidebarOpen} />
                  <div className="flex flex-col flex-1">
                    <Topbar
                      sidebarOpen={sidebarOpen}
                      setSidebarOpen={setSidebarOpen}
                      darkMode={darkMode}
                      toggleDarkMode={toggleDarkMode}
                    />
                    <main className="flex-1 overflow-y-auto p-4 md:p-6">
                      <Dashboard />
                    </main>
                  </div>
                </div>
                <Footer />
              </div>
            }
          />
          <Route
            path="/stores/:storeId/suppliers"
            element={
              <div className="flex flex-col min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
                <div className="flex flex-1">
                  <Sidebar open={sidebarOpen} setOpen={setSidebarOpen} />
                  <div className="flex flex-col flex-1">
                    <Topbar
                      sidebarOpen={sidebarOpen}
                      setSidebarOpen={setSidebarOpen}
                      darkMode={darkMode}
                      toggleDarkMode={toggleDarkMode}
                    />
                    <main className="flex-1 overflow-y-auto p-4 md:p-6">
                      <Suppliers />
                    </main>
                  </div>
                </div>
                <Footer />
              </div>
            }
          />
          <Route
            path="/stores/:storeId/suppliers/:id"
            element={
              <div className="flex flex-col min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
                <div className="flex flex-1">
                  <Sidebar open={sidebarOpen} setOpen={setSidebarOpen} />
                  <div className="flex flex-col flex-1">
                    <Topbar
                      sidebarOpen={sidebarOpen}
                      setSidebarOpen={setSidebarOpen}
                      darkMode={darkMode}
                      toggleDarkMode={toggleDarkMode}
                    />
                    <main className="flex-1 overflow-y-auto p-4 md:p-6">
                      <PartyDetails />
                    </main>
                  </div>
                </div>
                <Footer />
              </div>
            }
          />
          <Route
            path="/stores/:storeId/customers"
            element={
              <div className="flex flex-col min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
                <div className="flex flex-1">
                  <Sidebar open={sidebarOpen} setOpen={setSidebarOpen} />
                  <div className="flex flex-col flex-1">
                    <Topbar
                      sidebarOpen={sidebarOpen}
                      setSidebarOpen={setSidebarOpen}
                      darkMode={darkMode}
                      toggleDarkMode={toggleDarkMode}
                    />
                    <main className="flex-1 overflow-y-auto p-4 md:p-6">
                      <Customers />
                    </main>
                  </div>
                </div>
                <Footer />
              </div>
            }
          />
          <Route
            path="/stores/:storeId/ledger"
            element={
              <div className="flex flex-col min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
                <div className="flex flex-1">
                  <Sidebar open={sidebarOpen} setOpen={setSidebarOpen} />
                  <div className="flex flex-col flex-1">
                    <Topbar
                      sidebarOpen={sidebarOpen}
                      setSidebarOpen={setSidebarOpen}
                      darkMode={darkMode}
                      toggleDarkMode={toggleDarkMode}
                    />
                    <main className="flex-1 overflow-y-auto p-4 md:p-6">
                      <Ledger />
                    </main>
                  </div>
                </div>
                <Footer />
              </div>
            }
          />
          <Route
            path="/stores/:storeId/inventory"
            element={
              <div className="flex flex-col min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
                <div className="flex flex-1">
                  <Sidebar open={sidebarOpen} setOpen={setSidebarOpen} />
                  <div className="flex flex-col flex-1">
                    <Topbar
                      sidebarOpen={sidebarOpen}
                      setSidebarOpen={setSidebarOpen}
                      darkMode={darkMode}
                      toggleDarkMode={toggleDarkMode}
                    />
                    <main className="flex-1 overflow-y-auto p-4 md:p-6">
                      <Inventory />
                    </main>
                  </div>
                </div>
                <Footer />
              </div>
            }
          />
          <Route
            path="/stores/:storeId/invoices"
            element={
              <div className="flex flex-col min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
                <div className="flex flex-1">
                  <Sidebar open={sidebarOpen} setOpen={setSidebarOpen} />
                  <div className="flex flex-col flex-1">
                    <Topbar
                      sidebarOpen={sidebarOpen}
                      setSidebarOpen={setSidebarOpen}
                      darkMode={darkMode}
                      toggleDarkMode={toggleDarkMode}
                    />
                    <main className="flex-1 overflow-y-auto p-4 md:p-6">
                      <Invoices />
                    </main>
                  </div>
                </div>
                <Footer />
              </div>
            }
          />
          <Route
            path="/stores/:storeId/quotations"
            element={
              <div className="flex flex-col min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
                <div className="flex flex-1">
                  <Sidebar open={sidebarOpen} setOpen={setSidebarOpen} />
                  <div className="flex flex-col flex-1">
                    <Topbar
                      sidebarOpen={sidebarOpen}
                      setSidebarOpen={setSidebarOpen}
                      darkMode={darkMode}
                      toggleDarkMode={toggleDarkMode}
                    />
                    <main className="flex-1 overflow-y-auto p-4 md:p-6">
                      <Quotations />
                    </main>
                  </div>
                </div>
                <Footer />
              </div>
            }
          />
          <Route
            path="/stores/:storeId/reports"
            element={
              <div className="flex flex-col min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
                <div className="flex flex-1">
                  <Sidebar open={sidebarOpen} setOpen={setSidebarOpen} />
                  <div className="flex flex-col flex-1">
                    <Topbar
                      sidebarOpen={sidebarOpen}
                      setSidebarOpen={setSidebarOpen}
                      darkMode={darkMode}
                      toggleDarkMode={toggleDarkMode}
                    />
                    <main className="flex-1 overflow-y-auto p-4 md:p-6">
                      <Reports />
                    </main>
                  </div>
                </div>
                <Footer />
              </div>
            }
          />
          <Route
            path="/stores/:storeId/settings"
            element={
              <div className="flex flex-col min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
                <div className="flex flex-1">
                  <Sidebar open={sidebarOpen} setOpen={setSidebarOpen} />
                  <div className="flex flex-col flex-1">
                    <Topbar
                      sidebarOpen={sidebarOpen}
                      setSidebarOpen={setSidebarOpen}
                      darkMode={darkMode}
                      toggleDarkMode={toggleDarkMode}
                    />
                    <main className="flex-1 overflow-y-auto p-4 md:p-6">
                      <Settings />
                    </main>
                  </div>
                </div>
                <Footer />
              </div>
            }
          />
        </Route>

        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </div>
  );
}

function Unauthorized() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Unauthorized</h2>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          You do not have permission to access this page.
        </p>
        <a href="/login" className="mt-4 inline-block text-indigo-600 hover:text-indigo-500">
          Go to Login
        </a>
      </div>
    </div>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

export default App;